import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useUnifiedAuth } from '../hooks/useUnifiedAuth';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import BookingTermsModal from './BookingTermsModal';
import AddToCalendar from './AddToCalendar';
import { generateBookingReceiptPdf } from '../utils/bookingReceiptPdf';
import { Reveal } from './site/motion';
import {
  Check,
  Gift,
  ChevronLeft,
  ChevronRight,
  Clock,
  Sparkles,
  Ticket,
  Wallet,
  CreditCard,
  Info,
  RefreshCw,
  CalendarCheck,
  Download,
  Link2,
  MapPin,
  ArrowRight,
  AlertCircle,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ProviderBookingFlow = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useUnifiedAuth();
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const params = useParams();

  // Get provider ID and service from URL params or location state
  // Priority: 1. URL path params (:providerId), 2. Query params (?providerId=...), 3. Location state
  const searchParams = new URLSearchParams(location.search);
  const providerId = params.providerId || searchParams.get('providerId') || location.state?.providerId || location.state?.provider?.id;
  const preSelectedServiceId = searchParams.get('service') || searchParams.get('serviceId') || location.state?.serviceId || location.state?.selectedService?.id;
  const preSelectedServiceName = searchParams.get('service_name') || searchParams.get('serviceName');
  const preSelectedService = location.state?.selectedService;
  // AI-assistant deep-link: pre-fill date / time so the user lands on review.
  const preSelectedDate = searchParams.get('date') || location.state?.preSelectedDate;
  const preSelectedTime = searchParams.get('time') || location.state?.preSelectedTime;
  const preSelectedStaffId = searchParams.get('staff') || location.state?.preSelectedStaffId;

  // Reschedule mode handling
  const rescheduleMode = location.state?.rescheduleMode || false;
  const rescheduleAppointmentId = location.state?.appointmentId;
  const isRescheduling = rescheduleMode && rescheduleAppointmentId;

  // State management
  const [provider, setProvider] = useState(null);
  const [services, setServices] = useState([]);
  const [selectedService, setSelectedService] = useState(null);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [availableSlots, setAvailableSlots] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [currentMonth, setCurrentMonth] = useState(0); // 0 = current month, 1 = next month, etc.
  const [originalAppointment, setOriginalAppointment] = useState(null); // For reschedule mode
  
  // Patient info
  const [patientName, setPatientName] = useState('');
  const [patientPhone, setPatientPhone] = useState('');
  const [patientEmail, setPatientEmail] = useState('');
  const [notes, setNotes] = useState('');

  // Wallet payment
  const [walletBalance, setWalletBalance] = useState(0);
  const [useWallet, setUseWallet] = useState(false);
  const [loadingWallet, setLoadingWallet] = useState(false);

  // Terms modal
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [timingWarning, setTimingWarning] = useState(null);

  // Promo code
  const [promoCode, setPromoCode] = useState('');
  const [promoValidating, setPromoValidating] = useState(false);
  const [promoResult, setPromoResult] = useState(null);
  const [promoError, setPromoError] = useState('');

  // Paid extras that take no appointment time (face mask, hot towel...)
  const [addOns, setAddOns] = useState([]);
  const [selectedAddOnIds, setSelectedAddOnIds] = useState([]);

  // Loyalty points
  const [loyalty, setLoyalty] = useState(null);
  const [pointsToRedeem, setPointsToRedeem] = useState(0);

  // Completed booking (success screen)
  const [bookingResult, setBookingResult] = useState(null);

  // Auto-populate user information when user data is available
  useEffect(() => {
    if (user) {
      // Set name - try various formats
      const userName = user.name || 
                      (user.first_name && user.last_name ? `${user.first_name} ${user.last_name}` : '') ||
                      user.first_name || 
                      user.email?.split('@')[0] || 
                      '';
      setPatientName(userName);
      
      // Set email
      setPatientEmail(user.email || '');
      
      // Set phone with +965 prefix if not already present
      let phone = user.phone || '';
      if (phone && !phone.startsWith('+965')) {
        // Remove any leading zeros or spaces
        phone = phone.replace(/^[0\s]+/, '');
        // Add +965 prefix
        phone = `+965 ${phone}`;
      }
      setPatientPhone(phone);
    }
  }, [user]);

  // Fetch wallet balance when user is available
  useEffect(() => {
    const fetchWalletBalance = async () => {
      const walletToken = user?.token || localStorage.getItem('sessionToken');
      if (user && walletToken) {
        setLoadingWallet(true);
        try {
          const response = await axios.get(
            `${BACKEND_URL}/api/wallet/balance`,
            { headers: { Authorization: `Bearer ${walletToken}` } }
          );
          setWalletBalance(response.data.balance || 0);
        } catch (error) {
          console.error('Error fetching wallet balance:', error);
          setWalletBalance(0);
        } finally {
          setLoadingWallet(false);
        }
      }
    };
    
    fetchWalletBalance();
  }, [user]);

  // Loyalty points balance (points are kept apart from wallet money)
  useEffect(() => {
    const fetchPoints = async () => {
      const token = user?.token || localStorage.getItem('sessionToken');
      if (!user || !token) return;
      try {
        const res = await axios.get(`${BACKEND_URL}/api/loyalty/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setLoyalty(res.data);
      } catch (error) {
        setLoyalty(null);
      }
    };
    fetchPoints();
  }, [user]);

  // Get booking window limit based on provider's subscription tier
  const getBookingWindowMonths = () => {
    const tier = provider?.subscription_tier || 'Free';
    switch (tier.toLowerCase()) {
      case 'free':
        return 3;
      case 'basic':
        return 6;
      case 'pro':
        return 18;
      case 'enterprise':
        return 36;
      default:
        return 3; // Default to free tier
    }
  };

  // Fetch provider data
  useEffect(() => {
    const fetchProvider = async () => {
      // If provider is already in location state, use it
      if (location.state?.provider) {
        console.log('✅ Provider loaded from state:', location.state.provider);
        setProvider(location.state.provider);
        
        // Set services from embedded array
        const providerServices = location.state.provider.services || [];
        setServices(providerServices);
        
        // CRITICAL: Use service from provider's embedded services, not from state
        // The service in state might be missing fields like assigned_staff
        if (preSelectedService && location.state.provider.services) {
          const fullService = location.state.provider.services.find(
            s => s.id === preSelectedService.id || s.name === preSelectedService.name
          );
          if (fullService) {
            console.log('✅ Using full service from provider:', fullService.name);
            console.log('   assigned_staff:', fullService.assigned_staff);
            setSelectedService(fullService);
            setCurrentStep((step) => (step > 2 ? step : 2)); // Skip to date selection
          } else {
            // Fallback to the passed service if not found
            setSelectedService(preSelectedService);
            setCurrentStep((step) => (step > 2 ? step : 2));
          }
        } else if (preSelectedServiceId || preSelectedServiceName) {
          const service = providerServices.find(
            s => s.id === preSelectedServiceId
              || (preSelectedServiceName && (s.name || '').toLowerCase() === preSelectedServiceName.toLowerCase())
          );
          if (service) {
            setSelectedService(service);
            setCurrentStep((step) => (step > 2 ? step : 2));
          }
        }
        
        setLoading(false);
        return;
      }

      if (!providerId) {
        setError('No provider ID provided');
        setLoading(false);
        return;
      }

      try {
        const response = await axios.get(`${BACKEND_URL}/api/healthcare-providers/${providerId}`);
        console.log('✅ Provider loaded:', response.data);
        setProvider(response.data);
        
        // Set services from embedded array
        const providerServices = response.data.services || [];
        setServices(providerServices);
        
        // Pre-select service if provided (by id or name from AI assistant deep-link)
        if (preSelectedServiceId || preSelectedServiceName) {
          const service = providerServices.find(
            s => s.id === preSelectedServiceId
              || (preSelectedServiceName && (s.name || '').toLowerCase() === preSelectedServiceName.toLowerCase())
          );
          if (service) {
            setSelectedService(service);
            setCurrentStep((step) => (step > 2 ? step : 2)); // Skip to date selection
          }
        }
        
        setLoading(false);
      } catch (err) {
        console.error('Error fetching provider:', err);
        setError('Failed to load provider information');
        setLoading(false);
      }
    };

    fetchProvider();
  }, [providerId, preSelectedServiceId, location.state]);

  // Fetch original appointment data when in reschedule mode
  useEffect(() => {
    const fetchOriginalAppointment = async () => {
      if (!isRescheduling || !rescheduleAppointmentId) return;
      
      try {
        const token = user?.token || localStorage.getItem('sessionToken');
        const response = await axios.get(
          `${BACKEND_URL}/api/appointments/${rescheduleAppointmentId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        console.log('📋 Original appointment loaded for reschedule:', response.data);
        setOriginalAppointment(response.data);
        
        // Pre-select the service from the original appointment
        if (response.data.service_id && services.length > 0) {
          const originalService = services.find(s => s.id === response.data.service_id);
          if (originalService) {
            setSelectedService(originalService);
            setCurrentStep((step) => (step > 2 ? step : 2)); // Skip to date/time selection
          }
        }
      } catch (err) {
        console.error('Error fetching original appointment:', err);
        // Continue anyway - user can still select new service/time
      }
    };
    
    fetchOriginalAppointment();
  }, [isRescheduling, rescheduleAppointmentId, user, services]);

  // Generate availability when service is selected
  useEffect(() => {
    if (!selectedService || !provider) return;

    console.log('🔄 Generating availability for:', provider.name);
    generateAvailability();
  }, [selectedService, provider]);

  const generateAvailability = () => {
    if (!provider || !selectedService) return;

    console.log('🔄 Generating availability for:', provider.name);
    console.log('📋 Selected service:', selectedService.name);
    console.log('👥 Service assigned_staff:', selectedService.assigned_staff);
    console.log('📅 Provider staff_schedules keys:', Object.keys(provider.staff_schedules || {}));

    const availability = {};
    const today = new Date();
    const staffSchedules = provider.staff_schedules || {};
    
    // Get booking window limit based on tier
    const bookingWindowMonths = getBookingWindowMonths();
    const maxDays = bookingWindowMonths * 30; // Approximate days
    
    console.log(`📆 Booking window: ${bookingWindowMonths} months (~${maxDays} days)`);
    
    // Generate availability for tier-limited period (starting from today, i=0)
    for (let i = 0; i <= maxDays; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const dateStr = `${year}-${month}-${day}`;
      
      const dayName = date.toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();
      
      // Check if provider is open this day
      const operatingHours = provider.operating_hours?.[dayName];
      if (!operatingHours || !operatingHours.open || !operatingHours.close) {
        availability[dateStr] = { slots: [], staff: {} };
        continue;
      }

      // Generate time slots based on operating hours
      const startHour = parseInt(operatingHours.open.split(':')[0]);
      const endHour = parseInt(operatingHours.close.split(':')[0]);
      
      const baseTimeSlots = [];
      for (let hour = startHour; hour < endHour; hour++) {
        baseTimeSlots.push(`${hour.toString().padStart(2, '0')}:00`);
        baseTimeSlots.push(`${hour.toString().padStart(2, '0')}:30`);
      }

      // For today, filter out past time slots.
      // IMPORTANT: build "today" as a local YYYY-MM-DD so it matches dateStr
      // (which uses local components). Using toISOString() returns UTC which
      // can be off-by-one near midnight in non-UTC timezones (e.g. Kuwait UTC+3).
      const todayLocalStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
      const isToday = dateStr === todayLocalStr;
      const currentTime = isToday ? `${today.getHours().toString().padStart(2, '0')}:${today.getMinutes().toString().padStart(2, '0')}` : '00:00';
      
      const availableBaseSlots = isToday 
        ? baseTimeSlots.filter(slot => slot > currentTime)
        : baseTimeSlots;

      // Check which staff can perform this service on this day
      const serviceStaff = selectedService.assigned_staff || [];
      const daySlots = {};

      // Debug for Tuesday
      if (dayName === 'tuesday' && i <= 7) {
        console.log(`\n🔍 TUESDAY ${dateStr} DEBUG:`);
        console.log('  Service staff:', serviceStaff);
        console.log('  Operating hours:', operatingHours);
      }

      serviceStaff.forEach(staffId => {
        const staffSchedule = staffSchedules[staffId]?.[dayName];
        
        // Debug for Tuesday
        if (dayName === 'tuesday' && i <= 7) {
          console.log(`  Staff ${staffId}:`, staffSchedule);
        }
        
        if (staffSchedule && staffSchedule.length > 0) {
          // Staff is working this day
          const staffName = provider.staff?.find(s => s.id === staffId)?.name || staffId;
          
          // Get staff's working hours
          const workPeriods = staffSchedule.filter(period => period.type === 'work');
          
          const availableTimes = [];
          workPeriods.forEach(period => {
            const periodStart = period.start;
            const periodEnd = period.end;
            
            // Filter time slots to staff's working hours
            availableBaseSlots.forEach(slot => {
              if (slot >= periodStart && slot < periodEnd) {
                availableTimes.push(slot);
              }
            });
          });

          if (availableTimes.length > 0) {
            daySlots[staffId] = {
              name: staffName,
              times: availableTimes
            };
          }
        }
      });

      // Combine all staff availability for this day
      const allTimes = new Set();
      Object.values(daySlots).forEach(staff => {
        staff.times.forEach(time => allTimes.add(time));
      });

      // Calculate total slots across all staff (not just unique times)
      const totalSlots = Object.values(daySlots).reduce((total, staff) => {
        return total + staff.times.length;
      }, 0);

      availability[dateStr] = {
        slots: Array.from(allTimes).sort(),
        staff: daySlots,
        totalSlots: totalSlots  // Add total slot count for display
      };

      // Debug for Tuesday
      if (dayName === 'tuesday' && i <= 7) {
        console.log('  Generated slots:', Array.from(allTimes).length);
        console.log('  Staff with slots:', Object.keys(daySlots));
      }
    }

    console.log('✅ Availability generated for', Object.keys(availability).length, 'days');
    setAvailableSlots(availability);
  };

  // ── AI-assistant deep-link: auto-jump to review when ?date= & ?time= are valid.
  // Runs once after availability is generated for the pre-selected service.
  const [hasAutoJumped, setHasAutoJumped] = useState(false);
  useEffect(() => {
    if (hasAutoJumped) return;
    if (!preSelectedDate || !preSelectedTime) return;
    if (!selectedService || !provider) return;
    if (!availableSlots || Object.keys(availableSlots).length === 0) return;

    const day = availableSlots[preSelectedDate];
    if (!day || !day.slots || day.slots.length === 0) return;

    // Use the staff member the link names, as long as they really have that slot
    const staffMap = day.staff || {};
    const hasSlot = (staffId) => (staffMap[staffId]?.times || []).includes(preSelectedTime);
    let matchedStaffId = preSelectedStaffId && hasSlot(preSelectedStaffId) ? preSelectedStaffId : null;
    if (!matchedStaffId) {
      matchedStaffId = Object.keys(staffMap).find(hasSlot) || null;
    }
    if (!matchedStaffId) return; // slot no longer available — leave the user on step 2

    console.log(`🤖 AI deep-link: auto-jumping to review for ${preSelectedDate} @ ${preSelectedTime}`);
    setSelectedDate(preSelectedDate);
    setSelectedTime(preSelectedTime);
    setSelectedStaff(matchedStaffId);
    setCurrentStep(4);
    setHasAutoJumped(true);
  }, [availableSlots, selectedService, provider, preSelectedDate, preSelectedTime, preSelectedStaffId, hasAutoJumped]);

  const handleServiceSelect = (service) => {
    setSelectedService(service);
    setSelectedStaff(null);
    setSelectedDate(null);
    setSelectedTime(null);
    setCurrentStep(2);
  };

  // Extras the provider sells on the spot - priced, but never add appointment time
  useEffect(() => {
    if (!provider?.id) return;
    axios
      .get(`${BACKEND_URL}/api/providers/${provider.id}/add-ons`)
      .then((res) => setAddOns(res.data.add_ons || []))
      .catch(() => setAddOns([]));
  }, [provider?.id]);

  const handleDateSelect = (dateStr) => {
    setSelectedDate(dateStr);
    setSelectedTime(null);
    setCurrentStep(3);
  };

  const handleTimeSelect = (time, staffId) => {
    setSelectedTime(time);
    setSelectedStaff(staffId);
    setCurrentStep(4);
  };

  const handleApplyPromo = async () => {
    if (!promoCode.trim()) return;
    setPromoValidating(true);
    setPromoError('');
    setPromoResult(null);
    try {
      const token = user?.token || localStorage.getItem('sessionToken');
      const res = await axios.post(`${BACKEND_URL}/api/promo-codes/validate`, {
        code: promoCode.trim(),
        user_id: user?.id || '',
        booking_amount: selectedService?.price || 0
      }, { headers: { Authorization: `Bearer ${token}` } });
      setPromoResult(res.data);
    } catch (err) {
      setPromoError(err.response?.data?.detail || t('booking.promo.invalidCode', 'Invalid promo code'));
      setPromoResult(null);
    }
    setPromoValidating(false);
  };

  const handleRemovePromo = () => {
    setPromoCode('');
    setPromoResult(null);
    setPromoError('');
  };

  const selectedAddOns = addOns.filter((a) => selectedAddOnIds.includes(a.id));
  const addOnsTotal = Number(
    selectedAddOns.reduce((sum, a) => sum + Number(a.price || 0), 0).toFixed(3)
  );

  const toggleAddOn = (id) => {
    setSelectedAddOnIds((ids) => (ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]));
  };

  const getFinalPrice = () => {
    const servicePrice = promoResult ? promoResult.final_amount : (selectedService?.price || 0);
    return Number((servicePrice + addOnsTotal).toFixed(3));
  };

  // Points are spent in whole dinars and can never exceed the bill
  const maxRedeemablePoints = Math.min(
    loyalty?.redeemable_points || 0,
    Math.floor(getFinalPrice()) * 100
  );
  const pointsDiscount = Number((pointsToRedeem / 100).toFixed(3));
  const getPayableNow = () => Number(Math.max(getFinalPrice() - pointsDiscount, 0).toFixed(3));
  const pointsToEarn = Math.round(getPayableNow() * 5);

  // Keep the points within the bill if it shrinks after they were chosen
  useEffect(() => {
    setPointsToRedeem((p) => Math.min(p, maxRedeemablePoints));
  }, [maxRedeemablePoints]);

  const handleCheckTermsAndBook = async () => {
    // Check booking timing
    try {
      const response = await axios.post(`${BACKEND_URL}/api/booking-policies/check-timing`, {
        appointment_date: selectedDate,
        appointment_time: selectedTime
      });
      
      setTimingWarning(response.data);
      setShowTermsModal(true);
    } catch (error) {
      console.error('Error checking timing:', error);
      setShowTermsModal(true);
    }
  };

  const handleAcceptTerms = async () => {
    setShowTermsModal(false);
    await handleConfirmBooking();
  };

  const handleConfirmBooking = async () => {
    try {
      const token = user?.token || localStorage.getItem('sessionToken');
      
      // Handle reschedule mode
      if (isRescheduling) {
        // Build reschedule URL with query params
        let rescheduleUrl = `${BACKEND_URL}/api/appointments/${rescheduleAppointmentId}/reschedule?new_date=${selectedDate}&new_time=${selectedTime}`;
        
        // Add new_staff_id if a staff member is selected
        if (selectedStaff) {
          rescheduleUrl += `&new_staff_id=${selectedStaff}`;
        }
        
        // Add new_service_id if service changed
        if (selectedService && originalAppointment?.service_id !== selectedService.id) {
          rescheduleUrl += `&new_service_id=${selectedService.id}`;
        }
        
        const response = await axios.put(
          rescheduleUrl,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        console.log('✅ Reschedule response:', response.data);
        
        let successMessage = 'Appointment rescheduled successfully!';
        if (response.data.staff_changed) {
          successMessage = 'Appointment rescheduled with a different staff member!';
        }
        if (response.data.service_changed) {
          if (response.data.refund_processed) {
            successMessage = `Appointment rescheduled! ${response.data.refund_amount} KD refunded to your wallet.`;
          } else if (response.data.charge_required) {
            successMessage = `Appointment rescheduled! Additional charge of ${response.data.charge_amount} KD required.`;
          }
        }
        
        navigate('/appointments', {
          state: { 
            message: successMessage,
            appointment: response.data
          }
        });
        return;
      }
      
      // Normal booking flow
      // Determine payment method
      const paymentMethod = useWallet ? 'wallet' : 'myfatoorah';
      
      // Check if wallet has sufficient balance (points already come off the top)
      const finalPrice = getPayableNow();
      if (useWallet && walletBalance < finalPrice) {
        setError(`Insufficient wallet balance. You have KD ${walletBalance.toFixed(3)} but need KD ${finalPrice.toFixed(3)}`);
        return;
      }
      
      const bookingData = {
        doctor_id: provider.id,
        provider_type: 'provider',
        service_id: selectedService.id,
        service_name: selectedService.name,
        appointment_date: selectedDate,
        appointment_time: selectedTime,
        duration: selectedService.duration,
        consultation_fee: selectedService.price,
        addon_ids: selectedAddOnIds,
        points_to_redeem: pointsToRedeem,
        staff_id: selectedStaff,
        patient_name: patientName,
        patient_phone: patientPhone,
        patient_email: patientEmail,
        notes: notes,
        payment_method: paymentMethod,
        terms_accepted: true,
        promo_code: promoResult ? promoResult.code : null
      };

      const response = await axios.post(
        `${BACKEND_URL}/api/appointments`,
        bookingData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      console.log('✅ Booking response:', response.data);
      
      const appointmentData = response.data;
      
      // Check if payment is required (MyFatoorah)
      if (appointmentData.requires_payment && appointmentData.payment_url) {
        console.log('🔄 Redirecting to payment:', appointmentData.payment_url);
        // Redirect to MyFatoorah payment page
        window.location.href = appointmentData.payment_url;
        return;
      }
      
      // Show the in-flow confirmation screen (wallet payment or free appointment)
      setBookingResult({
        ...appointmentData,
        service_name: appointmentData.service_name || selectedService.name,
        appointment_date: appointmentData.appointment_date || selectedDate,
        appointment_time: appointmentData.appointment_time || selectedTime,
        patient_name: appointmentData.patient_name || patientName,
        patient_phone: appointmentData.patient_phone || patientPhone,
        patient_email: appointmentData.patient_email || patientEmail,
        doctor_name: appointmentData.doctor_name || provider.name,
        consultation_fee: appointmentData.consultation_fee ?? getFinalPrice(),
        staff_name:
          appointmentData.staff_name || provider.staff?.find((s) => s.id === selectedStaff)?.name || null,
        paid_from_wallet: useWallet,
      });
      window.scrollTo({ top: 0, behavior: 'smooth' });
      setCurrentStep(5);
      // High-intent moment — offer the "add to home screen" prompt
      window.dispatchEvent(new Event('enayahub:install-prompt'));
    } catch (error) {
      console.error('Error booking appointment:', error);
      const errorDetail = error.response?.data?.detail;
      // Handle structured error response from reschedule endpoint
      if (typeof errorDetail === 'object' && errorDetail.message) {
        setError(errorDetail.message);
      } else {
        setError(errorDetail || 'Failed to complete operation');
      }
    }
  };

  const generateCalendarDates = () => {
    if (!provider) return [];
    
    const dates = [];
    const today = new Date();
    
    // Calculate the target month
    const targetDate = new Date(today);
    targetDate.setMonth(today.getMonth() + currentMonth);
    
    const year = targetDate.getFullYear();
    const month = targetDate.getMonth();
    
    // Get first and last day of the month, normalized to start-of-day so
    // time-of-day on `today` does not accidentally exclude the final day.
    const firstDay = new Date(year, month, 1, 0, 0, 0, 0);
    const lastDay = new Date(year, month + 1, 0, 23, 59, 59, 999);
    
    // Start from today if it's the current month, otherwise from the 1st.
    // Normalise to start-of-day so the loop's `<= lastDay` check stays reliable.
    let startDate;
    if (currentMonth === 0) {
      // Current month - start from today (allow same-day bookings)
      startDate = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 0, 0, 0, 0);
    } else {
      // Future months - start from 1st
      startDate = firstDay;
    }
    
    // Generate dates for this month
    for (let date = new Date(startDate); date <= lastDay; date.setDate(date.getDate() + 1)) {
      const dateYear = date.getFullYear();
      const dateMonth = String(date.getMonth() + 1).padStart(2, '0');
      const dateDay = String(date.getDate()).padStart(2, '0');
      const dateStr = `${dateYear}-${dateMonth}-${dateDay}`;
      
      const dayAvailability = availableSlots[dateStr];
      const hasSlots = dayAvailability && dayAvailability.slots.length > 0;
      
      dates.push({
        date: dateStr,
        display: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        dayName: date.toLocaleDateString('en-US', { weekday: 'short' }),
        fullDate: new Date(date),
        hasSlots: hasSlots,
        slotCount: hasSlots ? (dayAvailability.totalSlots || dayAvailability.slots.length) : 0
      });
    }
    
    return dates;
  };

  const getMonthName = () => {
    const today = new Date();
    const targetDate = new Date(today);
    targetDate.setMonth(today.getMonth() + currentMonth);
    return targetDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  const canNavigateNext = () => {
    const bookingWindowMonths = getBookingWindowMonths();
    return currentMonth < bookingWindowMonths - 1;
  };

  const canNavigatePrevious = () => {
    return currentMonth > 0;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">{t('booking.loading', 'Loading provider information...')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 text-lg">{error}</p>
          <button
            onClick={() => navigate('/healthcare-providers')}
            className="mt-4 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            {t('booking.backToProviders', 'Back to Providers')}
          </button>
        </div>
      </div>
    );
  }

  const stepLabels = [
    t('booking.steps.selectService', 'Service'),
    t('booking.steps.chooseDate', 'Date'),
    t('booking.steps.pickTime', 'Time'),
    t('booking.steps.confirmDetails', 'Confirm'),
  ];

  const summaryRows = [
    selectedService && [t('booking.service', 'Service'), selectedService.name],
    selectedService && [
      t('booking.duration', 'Duration'),
      `${selectedService.duration} ${t('booking.mins', 'mins')}`,
    ],
    selectedDate && [t('booking.date', 'Date'), selectedDate],
    selectedTime && [t('booking.time', 'Time'), selectedTime],
    selectedStaff && [
      t('booking.staff', 'Staff'),
      provider.staff?.find((s) => s.id === selectedStaff)?.name || '—',
    ],
  ].filter(Boolean);

  return (
    <div
      className="relative min-h-screen overflow-hidden bg-gray-50 dark:bg-gray-950 transition-colors duration-300"
      dir={isRTL ? 'rtl' : 'ltr'}
      data-testid="booking-flow"
    >
      <span className="aurora-blob w-[28rem] h-[28rem] -top-44 start-[-8rem] bg-blue-300/40 dark:bg-blue-600/25 animate-aurora-slow" />

      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
        {isRescheduling && (
          <div className="mb-6 flex items-start gap-3 rounded-3xl border border-amber-200 dark:border-amber-400/20 bg-amber-50 dark:bg-amber-500/10 p-5">
            <RefreshCw className="mt-0.5 w-5 h-5 shrink-0 text-amber-600" />
            <div>
              <h3 className="font-display font-bold text-amber-900 dark:text-amber-200">
                {t('booking.rescheduling.title', 'Rescheduling Appointment')}
              </h3>
              <p className="mt-1 text-sm text-amber-800 dark:text-amber-300/80">
                {t('booking.rescheduling.instruction', 'Select a new date, time, and staff member for your appointment.')}
                {originalAppointment && (
                  <span className="block mt-1 numeric-ltr">
                    {t('booking.rescheduling.current', 'Current')}: {originalAppointment.appointment_date}{' '}
                    {t('booking.at', 'at')} {originalAppointment.appointment_time}
                  </span>
                )}
              </p>
            </div>
          </div>
        )}

        {/* Provider header */}
        <Reveal className="rounded-3xl border border-gray-200/80 dark:border-white/10 bg-white dark:bg-gray-900/60 p-6 sm:p-7">
          <p className="text-[11px] font-semibold uppercase tracking-widest text-blue-600 dark:text-blue-400">
            {provider.facility_type || t('booking.provider', 'Provider')}
          </p>
          <h1 className="font-display mt-2 text-2xl sm:text-3xl font-extrabold tracking-tight text-gray-900 dark:text-white">
            {provider.name}
          </h1>
          <p className="mt-2 inline-flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
            <MapPin className="w-4 h-4 text-teal-500" />
            {[provider.address, provider.area, provider.governorate].filter(Boolean).join(' · ')}
          </p>
        </Reveal>

        {/* Step rail */}
        {currentStep < 5 && (
          <div className="mt-6 rounded-3xl border border-gray-200/80 dark:border-white/10 bg-white dark:bg-gray-900/60 p-5" data-testid="booking-step-rail">
            <div className="flex items-center">
              {stepLabels.map((label, idx) => {
                const stepNo = idx + 1;
                const done = currentStep > stepNo;
                const active = currentStep === stepNo;
                return (
                  <React.Fragment key={label}>
                    <div className="flex items-center gap-2.5 shrink-0">
                      <span
                        className={`grid place-items-center w-9 h-9 rounded-full text-sm font-bold transition-all duration-500 ${
                          done
                            ? 'bg-emerald-500 text-white shadow-md shadow-emerald-500/30'
                            : active
                            ? 'bg-gradient-to-br from-blue-600 to-emerald-500 text-white shadow-md shadow-blue-600/30 scale-110'
                            : 'bg-gray-100 dark:bg-white/5 text-gray-400'
                        }`}
                      >
                        {done ? <Check className="w-4 h-4" strokeWidth={3} /> : stepNo}
                      </span>
                      <span
                        className={`hidden sm:block text-[13px] transition-colors ${
                          active
                            ? 'font-bold text-gray-900 dark:text-white'
                            : 'font-medium text-gray-400 dark:text-gray-500'
                        }`}
                      >
                        {label}
                      </span>
                    </div>
                    {idx < stepLabels.length - 1 && (
                      <span className="mx-2 sm:mx-3 h-0.5 flex-1 rounded-full bg-gray-100 dark:bg-white/10 overflow-hidden">
                        <span
                          className={`block h-full rounded-full bg-gradient-to-r from-blue-600 to-emerald-500 transition-transform duration-700 origin-left rtl:origin-right ${
                            currentStep > stepNo ? 'scale-x-100' : 'scale-x-0'
                          }`}
                        />
                      </span>
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        )}

        {/* Success screen */}
        {currentStep === 5 && bookingResult && (
          <Reveal className="mt-6" data-testid="booking-success-screen">
            <div className="relative overflow-hidden rounded-[2rem] border border-gray-200/80 dark:border-white/10 bg-white dark:bg-gray-900/60 p-7 sm:p-10 grain">
              <span className="aurora-blob w-72 h-72 -top-24 end-[-4rem] bg-emerald-300/50 dark:bg-emerald-500/25 animate-aurora" />
              <div className="relative">
                <span className="grid place-items-center w-16 h-16 rounded-3xl bg-gradient-to-br from-emerald-500 to-teal-500 shadow-lg shadow-emerald-500/30 animate-float-slow">
                  <Check className="w-8 h-8 text-white" strokeWidth={3} />
                </span>
                <h2 className="font-display mt-6 text-3xl sm:text-4xl font-extrabold tracking-tight text-gray-900 dark:text-white">
                  {t('booking.success.title', "You're booked!")}
                </h2>
                <p className="mt-3 max-w-lg text-base text-gray-600 dark:text-gray-400">
                  {t('booking.success.subtitle', 'A confirmation has been sent to you by WhatsApp and email.')}
                </p>

                {bookingResult.reference_number && (
                  <div className="mt-7 inline-flex flex-col rounded-2xl bg-gradient-to-r from-blue-600 to-emerald-500 px-5 py-4 shadow-lg shadow-blue-600/20">
                    <span className="text-[10px] font-bold uppercase tracking-widest text-white/70">
                      {t('booking.success.reference', 'Booking reference')}
                    </span>
                    <span
                      className="mt-1 font-mono text-lg font-bold text-white numeric-ltr"
                      data-testid="booking-reference"
                    >
                      {bookingResult.reference_number}
                    </span>
                  </div>
                )}

                <dl className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
                  {[
                    [t('booking.provider', 'Provider'), bookingResult.doctor_name],
                    [t('booking.service', 'Service'), bookingResult.service_name],
                    [t('booking.date', 'Date'), bookingResult.appointment_date],
                    [t('booking.time', 'Time'), bookingResult.appointment_time],
                    [t('booking.staff', 'Staff'), bookingResult.staff_name],
                    [
                      t('booking.total', 'Total'),
                      `${Number(bookingResult.amount_payable ?? bookingResult.consultation_fee ?? 0).toFixed(3)} ${t('booking.currency', 'KD')}`,
                    ],
                  ]
                    .filter(([, v]) => v)
                    .map(([label, value]) => (
                      <div key={label} className="border-b border-gray-100 dark:border-white/10 pb-3">
                        <dt className="text-xs font-semibold uppercase tracking-wide text-gray-400">{label}</dt>
                        <dd className="mt-1 text-sm font-semibold text-gray-900 dark:text-white numeric-ltr">{value}</dd>
                      </div>
                    ))}
                </dl>

                {(bookingResult.points_earned > 0 || bookingResult.points_redeemed > 0) && (
                  <div className="mt-6 rounded-2xl bg-emerald-50 dark:bg-emerald-500/10 px-5 py-4 text-start" data-testid="booking-success-points">
                    {bookingResult.points_redeemed > 0 && (
                      <p className="numeric-ltr text-sm font-semibold text-emerald-800 dark:text-emerald-300">
                        {t('loyalty.usedOnBooking', 'You used {{points}} points ({{value}} {{currency}}) on this booking.', {
                          points: bookingResult.points_redeemed,
                          value: Number(bookingResult.points_discount || 0).toFixed(3),
                          currency: t('booking.currency', 'KD'),
                        })}
                      </p>
                    )}
                    {bookingResult.points_earned > 0 && (
                      <p className="numeric-ltr mt-1 text-sm font-semibold text-emerald-800 dark:text-emerald-300">
                        {t('loyalty.earnedOnBooking', 'You earned {{points}} points — worth {{value}} {{currency}} off a future booking.', {
                          points: bookingResult.points_earned,
                          value: (bookingResult.points_earned / 100).toFixed(3),
                          currency: t('booking.currency', 'KD'),
                        })}
                      </p>
                    )}
                  </div>
                )}


                <div className="mt-9 flex flex-wrap gap-3">
                  <button
                    onClick={() => generateBookingReceiptPdf(bookingResult)}
                    data-testid="booking-receipt-download-btn"
                    className="btn-shine inline-flex items-center gap-2 rounded-full bg-gray-900 dark:bg-white px-6 py-3.5 text-sm font-semibold text-white dark:text-gray-900 lift"
                  >
                    <Download className="w-4 h-4" />
                    {t('booking.success.downloadReceipt', 'Download receipt')}
                  </button>
                  <div className="min-w-[190px]">
                    <AddToCalendar appointment={bookingResult} compact />
                  </div>
                  {bookingResult.reschedule_url && (
                    <a
                      href={bookingResult.reschedule_url}
                      data-testid="booking-manage-link"
                      className="inline-flex items-center gap-2 rounded-full border border-gray-200 dark:border-white/15 px-6 py-3.5 text-sm font-semibold text-gray-800 dark:text-gray-100 lift hover:border-blue-300 dark:hover:border-blue-400/40"
                    >
                      <Link2 className="w-4 h-4" />
                      {t('booking.success.manage', 'Manage booking')}
                    </a>
                  )}
                </div>

                <div className="mt-8 flex flex-wrap gap-3">
                  <button
                    onClick={() => navigate('/appointments')}
                    data-testid="booking-success-appointments-btn"
                    className="group inline-flex items-center gap-2 text-sm font-semibold text-blue-600 dark:text-blue-400"
                  >
                    {t('booking.success.myAppointments', 'Go to my appointments')}
                    <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1 rtl:rotate-180" />
                  </button>
                  <span className="text-gray-300 dark:text-white/20">·</span>
                  <button
                    onClick={() => navigate('/healthcare-providers')}
                    className="text-sm font-semibold text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
                  >
                    {t('booking.backToProviders', 'Back to Providers')}
                  </button>
                </div>
              </div>
            </div>
          </Reveal>
        )}

        {currentStep < 5 && (
          <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
              {/* Step 1: Service */}
              {currentStep === 1 && (
                <Reveal className="rounded-3xl border border-gray-200/80 dark:border-white/10 bg-white dark:bg-gray-900/60 p-6 sm:p-7">
                  <h2 className="font-display text-xl font-bold text-gray-900 dark:text-white">
                    {t('booking.selectService', 'Select a Service')}
                  </h2>
                  {services.length === 0 ? (
                    <p className="mt-4 text-sm text-gray-500">{t('booking.noServices', 'No services available')}</p>
                  ) : (
                    <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {services.map((service) => (
                        <button
                          key={service.id}
                          onClick={() => handleServiceSelect(service)}
                          data-testid="booking-service-card"
                          className={`group text-start rounded-2xl border p-5 transition-all duration-300 lift ${
                            selectedService?.id === service.id
                              ? 'border-blue-500 bg-blue-50/60 dark:bg-blue-500/10 shadow-md'
                              : 'border-gray-200 dark:border-white/10 hover:border-blue-300 dark:hover:border-blue-400/40 hover:shadow-lg'
                          }`}
                        >
                          <div className="flex items-start justify-between gap-3">
                            <h3 className="font-display font-bold text-gray-900 dark:text-white">{service.name}</h3>
                            <Sparkles className="w-4 h-4 shrink-0 text-pink-500 opacity-0 group-hover:opacity-100 transition-opacity" />
                          </div>
                          {service.description && (
                            <p className="mt-1.5 text-sm text-gray-500 dark:text-gray-400 line-clamp-2">
                              {service.description}
                            </p>
                          )}
                          <div className="mt-4 flex items-center justify-between">
                            <span className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-500 dark:text-gray-400">
                              <Clock className="w-3.5 h-3.5" />
                              <span className="numeric-ltr">{service.duration}</span> {t('booking.mins', 'mins')}
                            </span>
                            <span className="font-display text-lg font-extrabold text-gray-900 dark:text-white numeric-ltr">
                              {service.price} <span className="text-xs font-semibold text-gray-400">{t('booking.currency', 'KWD')}</span>
                            </span>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </Reveal>
              )}

              {/* Step 2: Date */}
              {currentStep === 2 && selectedService && (
                <Reveal className="rounded-3xl border border-gray-200/80 dark:border-white/10 bg-white dark:bg-gray-900/60 p-6 sm:p-7">
                  <h2 className="font-display text-xl font-bold text-gray-900 dark:text-white">
                    {t('booking.selectDate', 'Choose a Date')}
                  </h2>

                  <div className="mt-5 flex items-center justify-between gap-3">
                    <button
                      onClick={() => setCurrentMonth((prev) => Math.max(0, prev - 1))}
                      disabled={!canNavigatePrevious()}
                      className="grid place-items-center w-10 h-10 rounded-full border border-gray-200 dark:border-white/10 text-gray-600 dark:text-gray-300 disabled:opacity-40 hover:border-blue-300 transition-colors"
                    >
                      {isRTL ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
                    </button>
                    <span className="font-display font-bold text-gray-900 dark:text-white">{getMonthName()}</span>
                    <button
                      onClick={() => setCurrentMonth((prev) => prev + 1)}
                      disabled={!canNavigateNext()}
                      className="grid place-items-center w-10 h-10 rounded-full border border-gray-200 dark:border-white/10 text-gray-600 dark:text-gray-300 disabled:opacity-40 hover:border-blue-300 transition-colors"
                    >
                      {isRTL ? <ChevronLeft className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                    </button>
                  </div>

                  <div className="mt-6 grid grid-cols-7 gap-1.5 sm:gap-2">
                    {[t('days.sun', 'Sun'), t('days.mon', 'Mon'), t('days.tue', 'Tue'), t('days.wed', 'Wed'), t('days.thu', 'Thu'), t('days.fri', 'Fri'), t('days.sat', 'Sat')].map((day) => (
                      <div key={day} className="py-2 text-center text-[11px] font-bold uppercase tracking-wide text-gray-400">
                        {day}
                      </div>
                    ))}

                    {(() => {
                      const dates = generateCalendarDates();
                      if (dates.length === 0) return null;
                      const firstDayOfWeek = dates[0].fullDate.getDay();
                      return Array.from({ length: firstDayOfWeek }).map((_, i) => (
                        <div key={`empty-${i}`} className="aspect-square" />
                      ));
                    })()}

                    {generateCalendarDates().map((dateObj) => (
                      <button
                        key={dateObj.date}
                        onClick={() => dateObj.hasSlots && handleDateSelect(dateObj.date)}
                        disabled={!dateObj.hasSlots}
                        data-testid={dateObj.hasSlots ? 'booking-date-available' : 'booking-date-disabled'}
                        className={`aspect-square rounded-2xl border text-center transition-all duration-300 ${
                          !dateObj.hasSlots
                            ? 'border-transparent bg-gray-50 dark:bg-white/[0.02] text-gray-300 dark:text-gray-600 cursor-not-allowed'
                            : selectedDate === dateObj.date
                            ? 'border-transparent bg-gradient-to-br from-blue-600 to-emerald-500 text-white shadow-lg shadow-blue-600/25 scale-105'
                            : 'border-gray-200 dark:border-white/10 text-gray-700 dark:text-gray-200 hover:border-blue-400 hover:bg-blue-50/60 dark:hover:bg-blue-500/10 hover:-translate-y-0.5'
                        }`}
                      >
                        <span className="block text-base font-bold numeric-ltr">{dateObj.fullDate.getDate()}</span>
                        {dateObj.hasSlots && (
                          <span
                            className={`block text-[10px] font-semibold numeric-ltr ${
                              selectedDate === dateObj.date ? 'text-white/80' : 'text-emerald-600 dark:text-emerald-400'
                            }`}
                          >
                            {dateObj.slotCount}
                          </span>
                        )}
                      </button>
                    ))}
                  </div>

                  <button
                    onClick={() => setCurrentStep(1)}
                    className="mt-7 inline-flex items-center gap-2 rounded-full border border-gray-200 dark:border-white/10 px-5 py-2.5 text-sm font-semibold text-gray-700 dark:text-gray-200 hover:border-blue-300 transition-colors"
                  >
                    {isRTL ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
                    {t('booking.backToServices', 'Back to Services')}
                  </button>
                </Reveal>
              )}

              {/* Step 3: Time */}
              {currentStep === 3 && selectedDate && (
                <Reveal className="rounded-3xl border border-gray-200/80 dark:border-white/10 bg-white dark:bg-gray-900/60 p-6 sm:p-7">
                  <h2 className="font-display text-xl font-bold text-gray-900 dark:text-white">
                    {t('booking.selectTime', 'Select Time')}
                  </h2>
                  <p className="mt-2 text-sm text-gray-500 dark:text-gray-400 numeric-ltr">{selectedDate}</p>

                  {availableSlots[selectedDate]?.staff && Object.keys(availableSlots[selectedDate].staff).length > 0 ? (
                    <div className="mt-6 space-y-7">
                      {Object.entries(availableSlots[selectedDate].staff).map(([staffId, staffData]) => (
                        <div key={staffId}>
                          <h3 className="text-xs font-bold uppercase tracking-widest text-gray-400">
                            {t('booking.with', 'With')} {staffData.name}
                          </h3>
                          <div className="mt-3 grid grid-cols-3 sm:grid-cols-5 gap-2">
                            {staffData.times.map((time) => (
                              <button
                                key={`${staffId}-${time}`}
                                onClick={() => handleTimeSelect(time, staffId)}
                                data-testid="booking-time-slot"
                                className={`rounded-xl border px-2 py-2.5 text-sm font-semibold numeric-ltr transition-all duration-300 ${
                                  selectedTime === time && selectedStaff === staffId
                                    ? 'border-transparent bg-gradient-to-r from-blue-600 to-emerald-500 text-white shadow-md shadow-blue-600/25'
                                    : 'border-gray-200 dark:border-white/10 text-gray-700 dark:text-gray-200 hover:border-blue-400 hover:bg-blue-50/60 dark:hover:bg-blue-500/10 hover:-translate-y-0.5'
                                }`}
                              >
                                {time}
                              </button>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="mt-5 text-sm text-gray-500">
                      {t('booking.noSlotsAvailable', 'No available time slots for this date')}
                    </p>
                  )}

                  <button
                    onClick={() => setCurrentStep(2)}
                    className="mt-7 inline-flex items-center gap-2 rounded-full border border-gray-200 dark:border-white/10 px-5 py-2.5 text-sm font-semibold text-gray-700 dark:text-gray-200 hover:border-blue-300 transition-colors"
                  >
                    {isRTL ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
                    {t('booking.backToDateSelection', 'Back to Date Selection')}
                  </button>
                </Reveal>
              )}

              {/* Step 4: Confirm */}
              {currentStep === 4 && (
                <Reveal className="space-y-6">
                  {isRescheduling && originalAppointment && (
                    <div className="rounded-3xl border border-rose-200 dark:border-rose-400/20 bg-rose-50 dark:bg-rose-500/10 p-6">
                      <h3 className="font-display font-bold text-rose-900 dark:text-rose-200">
                        {t('booking.originalAppointment', 'Original Appointment')}
                      </h3>
                      <p className="mt-2 text-sm text-rose-800 dark:text-rose-300/80 numeric-ltr">
                        {originalAppointment.appointment_date} · {originalAppointment.appointment_time}
                      </p>
                    </div>
                  )}

                  {/* Extras - no extra time, straight onto the bill */}
                  {!isRescheduling && addOns.length > 0 && (
                    <div
                      className="rounded-3xl border border-gray-200/80 dark:border-white/10 bg-white dark:bg-gray-900/60 p-6"
                      data-testid="booking-addons-section"
                    >
                      <h3 className="flex items-center gap-2 font-display font-bold text-gray-900 dark:text-white">
                        <Sparkles className="w-4 h-4 text-amber-500" />
                        {t('addOns.bookingTitle', 'Add a little extra')}
                      </h3>
                      <p className="mt-1.5 text-sm text-gray-500 dark:text-gray-400">
                        {t('addOns.bookingSubtitle', 'These take no extra time — they are simply added to your bill.')}
                      </p>
                      <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {addOns.map((addon) => {
                          const picked = selectedAddOnIds.includes(addon.id);
                          return (
                            <label
                              key={addon.id}
                              data-testid="booking-addon-option"
                              className={`flex cursor-pointer items-start gap-3 rounded-2xl border p-4 transition-all duration-300 ${
                                picked
                                  ? 'border-amber-500 bg-amber-50/70 dark:bg-amber-500/10'
                                  : 'border-gray-200 dark:border-white/10 hover:border-amber-300'
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={picked}
                                onChange={() => toggleAddOn(addon.id)}
                                data-testid={`booking-addon-${addon.id}`}
                                className="mt-1 h-4 w-4 accent-amber-500"
                              />
                              <span className="flex-1">
                                <span className="flex items-center justify-between gap-3">
                                  <span className="font-semibold text-gray-900 dark:text-white">{addon.name}</span>
                                  <span className="font-display font-extrabold text-gray-900 dark:text-white numeric-ltr">
                                    +{Number(addon.price).toFixed(3)}{' '}
                                    <span className="text-xs font-semibold text-gray-400">{t('booking.currency', 'KD')}</span>
                                  </span>
                                </span>
                                {addon.description && (
                                  <span className="mt-1 block text-sm text-gray-500 dark:text-gray-400">{addon.description}</span>
                                )}
                              </span>
                            </label>
                          );
                        })}
                      </div>
                      {addOnsTotal > 0 && (
                        <p className="mt-4 rounded-xl bg-amber-50 dark:bg-amber-500/10 px-4 py-2.5 text-sm font-semibold text-amber-800 dark:text-amber-200 numeric-ltr" data-testid="booking-addons-total">
                          {t('addOns.addedToBill', 'Extras added')}: {addOnsTotal.toFixed(3)} {t('booking.currency', 'KD')}
                        </p>
                      )}
                    </div>
                  )}

                  {/* Loyalty points - 5% back, online bookings only */}
                  {!isRescheduling && loyalty && (
                    <div
                      className="rounded-3xl border border-emerald-200/80 dark:border-emerald-500/20 bg-emerald-50/50 dark:bg-emerald-500/5 p-6"
                      data-testid="booking-points-section"
                    >
                      <div className="flex flex-wrap items-center justify-between gap-3">
                        <h3 className="flex items-center gap-2 font-display font-bold text-gray-900 dark:text-white">
                          <Gift className="w-4 h-4 text-emerald-600" />
                          {t('loyalty.title', 'EnayaHub points')}
                        </h3>
                        <span className="rounded-full bg-white dark:bg-white/10 px-3 py-1 text-xs font-bold text-emerald-700 dark:text-emerald-300" data-testid="booking-points-balance">
                          <bdi>{loyalty.points_balance} {t('loyalty.points', 'points')}</bdi>
                          {' · '}
                          <bdi>{loyalty.value_kwd.toFixed(3)} {t('booking.currency', 'KD')}</bdi>
                        </span>
                      </div>

                      {maxRedeemablePoints >= 100 ? (
                        <>
                          <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
                            {t('loyalty.useHint', 'Use your points in whole dinars — 100 points = 1 KWD off this booking.')}
                          </p>
                          <div className="mt-4 flex flex-wrap items-center gap-3">
                            <div className="flex items-center gap-2 rounded-full bg-white dark:bg-white/10 p-1.5">
                              <button
                                type="button"
                                onClick={() => setPointsToRedeem((p) => Math.max(0, p - 100))}
                                disabled={pointsToRedeem === 0}
                                data-testid="points-decrease-btn"
                                className="grid h-8 w-8 place-items-center rounded-full bg-emerald-600 text-white font-bold disabled:opacity-40"
                              >
                                −
                              </button>
                              <span className="numeric-ltr min-w-[5.5rem] text-center text-sm font-extrabold text-gray-900 dark:text-white" data-testid="points-selected">
                                {pointsToRedeem}
                              </span>
                              <button
                                type="button"
                                onClick={() => setPointsToRedeem((p) => Math.min(maxRedeemablePoints, p + 100))}
                                disabled={pointsToRedeem >= maxRedeemablePoints}
                                data-testid="points-increase-btn"
                                className="grid h-8 w-8 place-items-center rounded-full bg-emerald-600 text-white font-bold disabled:opacity-40"
                              >
                                +
                              </button>
                            </div>
                            <button
                              type="button"
                              onClick={() => setPointsToRedeem(maxRedeemablePoints)}
                              data-testid="points-use-max-btn"
                              className="text-xs font-bold text-emerald-700 dark:text-emerald-300 hover:underline"
                            >
                              {t('loyalty.useMax', 'Use max')}
                            </button>
                            {pointsToRedeem > 0 && (
                              <span className="numeric-ltr text-sm font-bold text-emerald-700 dark:text-emerald-300" data-testid="points-discount-preview">
                                <bdi>−{pointsDiscount.toFixed(3)} {t('booking.currency', 'KD')}</bdi>
                              </span>
                            )}
                          </div>
                        </>
                      ) : (
                        <p className="mt-2 text-sm text-gray-600 dark:text-gray-300" data-testid="booking-points-too-few">
                          {t('loyalty.notEnough', 'You need 100 points (1 KWD) before you can spend them.')}
                        </p>
                      )}

                      <p className="mt-4 text-xs font-semibold text-emerald-800 dark:text-emerald-300" data-testid="booking-points-earn-preview">
                        {t('loyalty.earnPreview', "You'll earn {{points}} points on this booking — 5% back, on online bookings only.", { points: pointsToEarn })}
                      </p>
                    </div>
                  )}

                  {/* Promo */}
                  {!isRescheduling && (
                    <div
                      className="rounded-3xl border border-gray-200/80 dark:border-white/10 bg-white dark:bg-gray-900/60 p-6"
                      data-testid="promo-code-section"
                    >
                      <h3 className="flex items-center gap-2 font-display font-bold text-gray-900 dark:text-white">
                        <Ticket className="w-4 h-4 text-violet-500" />
                        {t('booking.promo.title', 'Have a Promo Code?')}
                      </h3>
                      {promoResult ? (
                        <div className="mt-4 rounded-2xl border border-emerald-200 dark:border-emerald-400/20 bg-emerald-50 dark:bg-emerald-500/10 p-4">
                          <div className="flex items-start justify-between gap-4">
                            <div>
                              <div className="flex items-center gap-2">
                                <span className="rounded-md bg-emerald-100 dark:bg-emerald-500/20 px-2 py-0.5 font-mono text-xs font-bold text-emerald-700 dark:text-emerald-300">
                                  {promoResult.code}
                                </span>
                                <span className="text-xs font-medium text-emerald-700 dark:text-emerald-300">
                                  {promoResult.message}
                                </span>
                              </div>
                              <div className="mt-3 space-y-1 text-sm numeric-ltr">
                                <div className="flex justify-between gap-8">
                                  <span className="text-gray-500">{t('booking.promo.originalFee', 'Original Fee')}</span>
                                  <span className="text-gray-700 dark:text-gray-200">
                                    {promoResult.original_amount.toFixed(3)} {t('booking.currency', 'KD')}
                                  </span>
                                </div>
                                <div className="flex justify-between gap-8">
                                  <span className="text-rose-500">{t('booking.promo.discount', 'Discount')}</span>
                                  <span className="font-medium text-rose-600">
                                    -{promoResult.discount_amount.toFixed(3)} {t('booking.currency', 'KD')}
                                  </span>
                                </div>
                                <div className="flex justify-between gap-8 border-t border-emerald-200 dark:border-emerald-400/20 pt-1">
                                  <span className="font-semibold text-emerald-800 dark:text-emerald-200">
                                    {t('booking.promo.finalFee', 'You Pay')}
                                  </span>
                                  <span className="font-bold text-emerald-700 dark:text-emerald-300">
                                    {promoResult.final_amount.toFixed(3)} {t('booking.currency', 'KD')}
                                  </span>
                                </div>
                              </div>
                            </div>
                            <button
                              onClick={handleRemovePromo}
                              data-testid="remove-promo-btn"
                              className="shrink-0 rounded-full border border-rose-200 px-3 py-1.5 text-xs font-semibold text-rose-600 hover:bg-rose-50"
                            >
                              {t('booking.promo.remove', 'Remove')}
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="mt-4">
                          <div className="flex gap-2">
                            <input
                              type="text"
                              value={promoCode}
                              onChange={(e) => { setPromoCode(e.target.value.toUpperCase()); setPromoError(''); }}
                              placeholder={t('booking.promo.placeholder', 'Enter promo code')}
                              data-testid="booking-promo-input"
                              className="flex-1 rounded-full border border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-white/5 px-5 py-3 font-mono text-sm uppercase text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-violet-500/40"
                            />
                            <button
                              onClick={handleApplyPromo}
                              disabled={!promoCode.trim() || promoValidating}
                              data-testid="apply-promo-btn"
                              className="rounded-full bg-violet-600 px-6 py-3 text-sm font-semibold text-white hover:bg-violet-700 disabled:opacity-40 transition-colors"
                            >
                              {promoValidating ? t('common.loading', 'Loading...') : t('booking.promo.apply', 'Apply')}
                            </button>
                          </div>
                          {promoError && (
                            <p className="mt-2 text-sm text-rose-500" data-testid="promo-error">{promoError}</p>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Your info */}
                  <div className="rounded-3xl border border-gray-200/80 dark:border-white/10 bg-white dark:bg-gray-900/60 p-6">
                    <h3 className="font-display font-bold text-gray-900 dark:text-white">
                      {t('booking.patientInfo.title', 'Your Information')}
                    </h3>
                    <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="sm:col-span-2">
                        <label className="block text-xs font-semibold uppercase tracking-wide text-gray-400 mb-2">
                          {t('booking.patientInfo.name', 'Full Name')} *
                        </label>
                        <input
                          type="text"
                          value={patientName}
                          onChange={(e) => setPatientName(e.target.value)}
                          data-testid="booking-name-input"
                          className="w-full rounded-2xl border border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-white/5 px-4 py-3 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500/40"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold uppercase tracking-wide text-gray-400 mb-2">
                          {t('booking.patientInfo.phone', 'Phone Number')} *
                        </label>
                        <input
                          type="tel"
                          value={patientPhone}
                          onChange={(e) => setPatientPhone(e.target.value)}
                          placeholder="+965 12345678"
                          data-testid="booking-phone-input"
                          className="w-full rounded-2xl border border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-white/5 px-4 py-3 text-sm text-gray-900 dark:text-white numeric-ltr focus:outline-none focus:ring-2 focus:ring-blue-500/40"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold uppercase tracking-wide text-gray-400 mb-2">
                          {t('booking.patientInfo.email', 'Email Address')} *
                        </label>
                        <input
                          type="email"
                          value={patientEmail}
                          onChange={(e) => setPatientEmail(e.target.value)}
                          data-testid="booking-email-input"
                          className="w-full rounded-2xl border border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-white/5 px-4 py-3 text-sm text-gray-900 dark:text-white numeric-ltr focus:outline-none focus:ring-2 focus:ring-blue-500/40"
                        />
                      </div>
                      <div className="sm:col-span-2">
                        <label className="block text-xs font-semibold uppercase tracking-wide text-gray-400 mb-2">
                          {t('booking.patientInfo.notes', 'Special Requests (Optional)')}
                        </label>
                        <textarea
                          value={notes}
                          onChange={(e) => setNotes(e.target.value)}
                          rows={3}
                          placeholder={t('booking.patientInfo.notesPlaceholder', 'Any additional information for the provider...')}
                          className="w-full rounded-2xl border border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-white/5 px-4 py-3 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500/40"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Payment */}
                  <div className="rounded-3xl border border-gray-200/80 dark:border-white/10 bg-white dark:bg-gray-900/60 p-6">
                    <h3 className="font-display font-bold text-gray-900 dark:text-white">
                      {t('booking.paymentMethod', 'Payment Method')}
                    </h3>

                    <div className="mt-5 space-y-3">
                      <label
                        className={`flex items-start gap-4 rounded-2xl border p-5 cursor-pointer transition-all duration-300 ${
                          useWallet
                            ? 'border-blue-500 bg-blue-50/60 dark:bg-blue-500/10'
                            : 'border-gray-200 dark:border-white/10 hover:border-blue-300'
                        }`}
                      >
                        <input
                          type="radio"
                          name="paymentMethod"
                          checked={useWallet}
                          onChange={() => setUseWallet(true)}
                          disabled={!walletBalance || walletBalance < getPayableNow()}
                          data-testid="payment-wallet-radio"
                          className="mt-1 w-4 h-4"
                        />
                        <span className="grid place-items-center w-10 h-10 shrink-0 rounded-xl bg-blue-50 dark:bg-blue-500/10">
                          <Wallet className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                        </span>
                        <span className="flex-1">
                          <span className="flex flex-wrap items-center gap-2">
                            <span className="font-semibold text-gray-900 dark:text-white">
                              {t('booking.payWithWallet', 'Pay with Wallet')}
                            </span>
                            <span className="rounded-full bg-gray-100 dark:bg-white/10 px-2 py-0.5 text-[11px] font-bold text-gray-600 dark:text-gray-300 numeric-ltr">
                              {loadingWallet ? '…' : `${Number(walletBalance).toFixed(3)} ${t('booking.currency', 'KD')}`}
                            </span>
                          </span>
                          <span className="mt-1 block text-sm text-gray-500 dark:text-gray-400">
                            {!walletBalance || walletBalance === 0
                              ? t('booking.noWalletBalance', 'No wallet balance available. Please top up your wallet first.')
                              : walletBalance < getPayableNow()
                              ? `${t('booking.currency', 'KD')} ${(getPayableNow() - walletBalance).toFixed(3)} ${t('booking.moreNeeded', 'more needed')}`
                              : t('booking.instantPayment', 'Instant payment from your wallet balance')}
                          </span>
                          {(!walletBalance || walletBalance < getPayableNow()) && (
                            <button
                              type="button"
                              onClick={(e) => { e.preventDefault(); navigate('/wallet'); }}
                              data-testid="booking-topup-wallet-link"
                              className="mt-2 inline-flex text-xs font-semibold text-blue-600 dark:text-blue-400 hover:underline"
                            >
                              {t('booking.topUpWallet', 'Top up my wallet')}
                            </button>
                          )}
                        </span>
                      </label>

                      <label
                        className={`flex items-start gap-4 rounded-2xl border p-5 cursor-pointer transition-all duration-300 ${
                          !useWallet
                            ? 'border-blue-500 bg-blue-50/60 dark:bg-blue-500/10'
                            : 'border-gray-200 dark:border-white/10 hover:border-blue-300'
                        }`}
                      >
                        <input
                          type="radio"
                          name="paymentMethod"
                          checked={!useWallet}
                          onChange={() => setUseWallet(false)}
                          data-testid="payment-card-radio"
                          className="mt-1 w-4 h-4"
                        />
                        <span className="grid place-items-center w-10 h-10 shrink-0 rounded-xl bg-emerald-50 dark:bg-emerald-500/10">
                          <CreditCard className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                        </span>
                        <span className="flex-1">
                          <span className="block font-semibold text-gray-900 dark:text-white">
                            {t('booking.payWithCard', 'Pay with Card/K-Net')}
                          </span>
                          <span className="mt-1 block text-sm text-gray-500 dark:text-gray-400">
                            {t('booking.securePayment', 'Secure payment via MyFatoorah')}
                          </span>
                        </span>
                      </label>
                    </div>
                  </div>

                  {/* Policies */}
                  <div className="rounded-3xl border border-blue-200/70 dark:border-blue-400/20 bg-blue-50/70 dark:bg-blue-500/[0.07] p-6">
                    <h3 className="flex items-center gap-2 font-display font-bold text-blue-900 dark:text-blue-200">
                      <Info className="w-4 h-4" />
                      {t('booking.policies.title', 'Important Policies - Please Read')}
                    </h3>
                    <ul className="mt-4 space-y-3 text-sm text-blue-900/80 dark:text-blue-100/70">
                      <li className="flex gap-2.5">
                        <Clock className="mt-0.5 w-4 h-4 shrink-0 text-blue-600" />
                        <span>
                          <strong>{t('booking.policies.arrivalTime', 'Arrival Time')}: </strong>
                          {t('booking.policies.arrivalTimeDesc', 'Please arrive 10 minutes before your appointment. If you arrive more than 15 minutes late, the provider has the right to not admit you, and no refund will be issued.')}
                        </span>
                      </li>
                      <li className="flex gap-2.5">
                        <Check className="mt-0.5 w-4 h-4 shrink-0 text-emerald-600" />
                        <span>
                          <strong>{t('booking.policies.cancellation', 'Free Cancellation')}: </strong>
                          {t('booking.policies.cancellationDesc', 'Cancel up to 24 hours before your appointment for a full refund to your wallet.')}
                        </span>
                      </li>
                      <li className="flex gap-2.5">
                        <RefreshCw className="mt-0.5 w-4 h-4 shrink-0 text-amber-600" />
                        <span>
                          <strong>{t('booking.policies.rescheduling', 'Rescheduling')}: </strong>
                          {t('booking.policies.reschedulingDesc', 'You can reschedule your appointment up to 3 hours before the appointment time. Once the appointment time has passed, you cannot reschedule.')}
                        </span>
                      </li>
                    </ul>
                  </div>

                  {error && (
                    <div className="flex items-start gap-3 rounded-2xl border border-rose-200 dark:border-rose-400/20 bg-rose-50 dark:bg-rose-500/10 p-4 text-sm text-rose-700 dark:text-rose-300">
                      <AlertCircle className="mt-0.5 w-4 h-4 shrink-0" />
                      {error}
                    </div>
                  )}

                  <div className="flex flex-col sm:flex-row gap-3">
                    <button
                      onClick={() => setCurrentStep(3)}
                      className="inline-flex items-center justify-center gap-2 rounded-full border border-gray-200 dark:border-white/10 px-6 py-3.5 text-sm font-semibold text-gray-700 dark:text-gray-200 hover:border-blue-300 transition-colors"
                    >
                      {isRTL ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
                      {t('common.back', 'Back')}
                    </button>
                    <button
                      onClick={handleCheckTermsAndBook}
                      disabled={!patientName || !patientPhone || !patientEmail}
                      data-testid="booking-confirm-btn"
                      className={`btn-shine flex-1 inline-flex items-center justify-center gap-2 rounded-full px-6 py-3.5 text-sm font-bold text-white shadow-lg transition-shadow disabled:opacity-40 disabled:shadow-none ${
                        isRescheduling
                          ? 'bg-amber-500 shadow-amber-500/25'
                          : 'bg-gradient-to-r from-blue-600 to-emerald-500 shadow-blue-600/25 hover:shadow-xl hover:shadow-emerald-500/25'
                      }`}
                    >
                      <CalendarCheck className="w-4 h-4" />
                      {isRescheduling
                        ? t('booking.confirmReschedule', 'Confirm Reschedule')
                        : t('booking.reviewAndConfirm', 'Review Terms & Confirm Booking')}
                    </button>
                  </div>
                </Reveal>
              )}
            </div>

            {/* Sticky summary */}
            <aside className="lg:col-span-1">
              <div className="lg:sticky lg:top-28 rounded-3xl border border-gray-200/80 dark:border-white/10 bg-white dark:bg-gray-900/60 p-6" data-testid="booking-summary-panel">
                <h3 className="text-xs font-bold uppercase tracking-widest text-gray-400">
                  {t('booking.bookingDetails', 'Booking Summary')}
                </h3>

                {summaryRows.length === 0 ? (
                  <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">
                    {t('booking.summaryEmpty', 'Pick a service to get started.')}
                  </p>
                ) : (
                  <dl className="mt-4 space-y-3">
                    {summaryRows.map(([label, value]) => (
                      <div key={label} className="flex items-start justify-between gap-4 border-b border-gray-100 dark:border-white/10 pb-3 last:border-0">
                        <dt className="text-xs font-semibold text-gray-400">{label}</dt>
                        <dd className="text-end text-sm font-semibold text-gray-900 dark:text-white numeric-ltr">{value}</dd>
                      </div>
                    ))}
                  </dl>
                )}

                {selectedAddOns.length > 0 && (
                  <div className="mt-4 rounded-2xl bg-amber-50 dark:bg-amber-500/10 p-4" data-testid="summary-addons">
                    <p className="text-[10px] font-bold uppercase tracking-widest text-amber-700 dark:text-amber-300">
                      {t('addOns.summaryTitle', 'Extras')}
                    </p>
                    <ul className="mt-2 space-y-1.5">
                      {selectedAddOns.map((addon) => (
                        <li key={addon.id} className="flex items-center justify-between gap-3 text-xs font-semibold text-amber-900 dark:text-amber-200">
                          <span>{addon.name}</span>
                          <span className="numeric-ltr">+{Number(addon.price).toFixed(3)}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {selectedService && !isRescheduling && (
                  <div className="mt-5 rounded-2xl bg-gradient-to-br from-blue-600 to-emerald-500 p-5">
                    <p className="text-[10px] font-bold uppercase tracking-widest text-white/70">
                      {t('booking.total', 'Total')}
                    </p>
                    <p className="mt-1 font-display text-2xl font-extrabold text-white numeric-ltr" data-testid="summary-total">
                      {getPayableNow().toFixed(3)} {t('booking.currency', 'KD')}
                    </p>
                    {pointsToRedeem > 0 && (
                      <p className="mt-1 text-xs font-semibold text-white/90" data-testid="summary-points-line">
                        {t('loyalty.pointsApplied', 'Points applied')}:{' '}
                        <bdi className="numeric-ltr">−{pointsDiscount.toFixed(3)} {t('booking.currency', 'KD')}</bdi>
                      </p>
                    )}
                    {promoResult && (
                      <p className="mt-1 text-xs text-white/70 line-through numeric-ltr">
                        {(selectedService.price + addOnsTotal).toFixed(3)} {t('booking.currency', 'KD')}
                      </p>
                    )}
                  </div>
                )}
              </div>
            </aside>
          </div>
        )}
      </div>

      <BookingTermsModal
        isOpen={showTermsModal}
        onClose={() => setShowTermsModal(false)}
        onAccept={handleAcceptTerms}
        bookingDetails={{
          appointment_date: selectedDate,
          appointment_time: selectedTime,
        }}
        timingWarning={timingWarning}
      />
    </div>
  );
};

export default ProviderBookingFlow;
